import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useAppDispatch } from 'hooks/useRedux';
import { useLazyBusinessListQuery } from 'store/apis/businessListApi';
import { setBusiness, setBusinesses } from 'store/slices/businessSlice';
import { http } from 'middleware/apiMiddleware';
import { verifyInviteApi, acceptInviteApi } from 'constants/apiUrls';
import { routes } from 'constants/routes';
import { Loader } from 'components/Common/Loader';
import toast from 'react-hot-toast';

const PENDING_INVITE_TOKEN_KEY = 'pendingInviteToken';
const SKIP_ONBOARDING_KEY = 'skipOnboardingAfterInvite';

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const extractEmailFromJwt = (token: string): string => {
  const payload = decodeJwtPayload(token);
  const dataField = typeof payload?.data === 'string' ? payload.data : '';
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  const match = dataField.match(uuidRegex);
  return match ? dataField.slice(match[0].length) : '';
};

const InviteHandler = () => {
  const { token } = useParams<{ token: string }>();
  const authContext = useAuth();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [, setSelectedBusiness] = useLocalStorage('selectedBusiness', null);
  const [fetchBusinesses] = useLazyBusinessListQuery();
  const [isProcessing, setIsProcessing] = useState(true);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    if (!token) {
      navigate(routes.home, { replace: true });
      return;
    }

    const isLoggedIn = !!authContext?.user;

    if (!isLoggedIn) {
      sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
      sessionStorage.setItem(SKIP_ONBOARDING_KEY, 'true');
      navigate(routes.login, { replace: true });
      return;
    }

    const processInvite = async () => {
      try {
        const { data: verifyData, error: verifyError } = await http.get(verifyInviteApi(token));

        if (verifyError) {
          sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
          sessionStorage.removeItem(SKIP_ONBOARDING_KEY);
          toast.error(verifyError?.message || 'Invalid or expired invite link');
          navigate(routes.home, { replace: true });
          return;
        }

        const invitedEmail = verifyData?.email || verifyData?.invitedEmail || '';
        const businessName = verifyData?.businessName || verifyData?.business?.name || '';
        const businessUuid = verifyData?.businessUuid || verifyData?.business?.uuid || '';

        const loggedInEmail = authContext.user?.email?.toLowerCase();
        if (invitedEmail && loggedInEmail && invitedEmail.toLowerCase() !== loggedInEmail) {
          sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
          sessionStorage.removeItem(SKIP_ONBOARDING_KEY);
          toast.error(
            `This invite is for ${invitedEmail}. Please sign in with the correct account.`,
          );
          setIsProcessing(false);
          navigate(routes.home, { replace: true });
          return;
        }

        const { error: acceptError } = await http.get(acceptInviteApi(token));

        if (acceptError) {
          toast.error(acceptError?.message || 'Failed to accept invite');
          navigate(routes.home, { replace: true });
          return;
        }

        const result = await fetchBusinesses();
        const businesses = result?.data || [];

        if (businesses.length > 0) {
          dispatch(setBusinesses(businesses));
        }

        if (businessUuid && businesses.length > 0) {
          const matched = businesses.find((b: EntityType) => b?.business?.uuid === businessUuid);
          if (matched?.business) {
            setSelectedBusiness({ ...matched.business });
            dispatch(setBusiness({ ...matched.business }));
          }
        }

        toast.success(
          businessName
            ? `You have accepted the invite for ${businessName}`
            : 'Invite accepted successfully',
        );

        navigate(routes.home, { replace: true });
      } catch {
        toast.error('Something went wrong while processing the invite');
        navigate(routes.home, { replace: true });
      } finally {
        setIsProcessing(false);
        sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
        sessionStorage.removeItem(SKIP_ONBOARDING_KEY);
      }
    };

    processInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, authContext?.user]);

  if (!isProcessing) return null;

  return (
    <div className="flex items-center justify-center h-screen bg-white">
      <div className="flex flex-col items-center gap-4">
        <Loader size={8} />
        <p className="font-poppins text-sm text-darkBlue">Processing your invite...</p>
      </div>
    </div>
  );
};

export { PENDING_INVITE_TOKEN_KEY, SKIP_ONBOARDING_KEY, extractEmailFromJwt };
export default InviteHandler;
